// suggestions.js
// Firebase suggestions collection API functions
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  increment
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const SUGGESTIONS_COLLECTION = 'suggestions';

/**
 * Get all suggestions ordered by votes
 */
export const getAllSuggestions = async () => {
  try {
    const suggestionsRef = collection(db, SUGGESTIONS_COLLECTION);
    const q = query(suggestionsRef, orderBy('votesCount', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const suggestions = [];
    querySnapshot.forEach((doc) => {
      suggestions.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    return suggestions;
  } catch (error) {
    console.error('Error getting suggestions:', error);
    throw error;
  }
};

/**
 * Get suggestion by ID
 */
export const getSuggestionById = async (suggestionId) => {
  try {
    const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
    const suggestionSnap = await getDoc(suggestionRef);
    
    if (suggestionSnap.exists()) {
      return {
        id: suggestionSnap.id,
        ...suggestionSnap.data(),
      };
    } else {
      throw new Error('Suggestion not found');
    }
  } catch (error) {
    console.error('Error getting suggestion:', error);
    throw error;
  }
};

/**
 * Create new suggestion
 * @param {string} userId - User ID creating the suggestion
 * @param {string} userName - User name
 * @param {string} text - Suggestion text
 */
export const createSuggestion = async (userId, userName, text) => {
  try {
    if (!text || !text.trim()) {
      throw new Error('Suggestion text is required');
    }

    const suggestionsRef = collection(db, SUGGESTIONS_COLLECTION);
    const docRef = await addDoc(suggestionsRef, {
      userId,
      userName,
      text: text.trim(),
      votesCount: 0,
      upvotes: [],
      downvotes: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating suggestion:', error);
    throw error;
  }
};

/**
 * Update suggestion
 * @param {string} suggestionId - Suggestion ID
 * @param {Object} updates - Fields to update
 */
export const updateSuggestion = async (suggestionId, updates) => {
  try {
    const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
    await updateDoc(suggestionRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating suggestion:', error);
    throw error;
  }
};

/**
 * Delete suggestion
 * @param {string} suggestionId - Suggestion ID
 */
export const deleteSuggestion = async (suggestionId) => {
  try {
    const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
    await deleteDoc(suggestionRef);
  } catch (error) {
    console.error('Error deleting suggestion:', error);
    throw error;
  }
};

/**
 * Vote on a suggestion (upvote or downvote)
 * @param {string} suggestionId - Suggestion ID
 * @param {string} userId - User ID
 * @param {string} voteType - 'up' or 'down'
 */
export const voteSuggestion = async (suggestionId, userId, voteType) => {
  try {
    const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
    const suggestionSnap = await getDoc(suggestionRef);
    
    if (!suggestionSnap.exists()) {
      throw new Error('Suggestion not found');
    }

    const suggestionData = suggestionSnap.data();
    const upvotes = suggestionData.upvotes || [];
    const downvotes = suggestionData.downvotes || [];
    
    const hasUpvoted = upvotes.includes(userId);
    const hasDownvoted = downvotes.includes(userId);

    let newUpvotes = [...upvotes];
    let newDownvotes = [...downvotes];
    let votesCountChange = 0;

    if (voteType === 'up') {
      if (hasUpvoted) {
        // User already upvoted, do nothing or could remove the upvote
        return;
      } else if (hasDownvoted) {
        // User had downvoted, remove downvote and add upvote
        newDownvotes = newDownvotes.filter(id => id !== userId);
        newUpvotes.push(userId);
        votesCountChange = 2; // Remove -1 and add +1
      } else {
        // New upvote
        newUpvotes.push(userId);
        votesCountChange = 1;
      }
    } else if (voteType === 'down') {
      if (hasDownvoted) {
        // User already downvoted, do nothing or could remove the downvote
        return;
      } else if (hasUpvoted) {
        // User had upvoted, remove upvote and add downvote
        newUpvotes = newUpvotes.filter(id => id !== userId);
        newDownvotes.push(userId);
        votesCountChange = -2; // Remove +1 and add -1
      } else {
        // New downvote
        newDownvotes.push(userId);
        votesCountChange = -1;
      }
    }

    await updateDoc(suggestionRef, {
      upvotes: newUpvotes,
      downvotes: newDownvotes,
      votesCount: increment(votesCountChange),
      updatedAt: serverTimestamp(),
    });

    return {
      upvotes: newUpvotes,
      downvotes: newDownvotes,
    };
  } catch (error) {
    console.error('Error voting on suggestion:', error);
    throw error;
  }
};

/**
 * Get user's vote status for a suggestion
 * @param {string} suggestionId - Suggestion ID
 * @param {string} userId - User ID
 * @returns {string|null} - 'up', 'down', or null
 */
export const getUserVote = async (suggestionId, userId) => {
  try {
    const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
    const suggestionSnap = await getDoc(suggestionRef);
    
    if (!suggestionSnap.exists()) {
      return null;
    }

    const suggestionData = suggestionSnap.data();
    const upvotes = suggestionData.upvotes || [];
    const downvotes = suggestionData.downvotes || [];
    
    if (upvotes.includes(userId)) {
      return 'up';
    } else if (downvotes.includes(userId)) {
      return 'down';
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user vote:', error);
    throw error;
  }
};
